import { SessionStatus } from '@threadline/contracts';
import { useI18n, type MessageKey, type Translate } from '@/i18n';
import { cn } from '@/lib/utils';

type Tone = 'ok' | 'warn' | 'danger' | 'idle' | 'info';

/**
 * The status vocabulary of the whole product lives here. Eleven states is a lot
 * for an operator to hold in their head, so each one collapses into one of five
 * tones - the colour tells you whether to care, the label tells you what is
 * happening.
 *
 * Only the tone is stored; the wording lives in the `status.*` dictionary keys,
 * which are derived from the enum member so the two cannot drift apart.
 */
const TONE: Record<SessionStatus, Tone> = {
  CREATED: 'idle',
  LAUNCHING: 'info',
  WAITING_QR: 'warn',
  QR_EXPIRED: 'warn',
  AUTHENTICATING: 'info',
  CONNECTED: 'ok',
  DISCONNECTED: 'danger',
  RECONNECTING: 'warn',
  LOGGED_OUT: 'idle',
  STOPPED: 'idle',
  ERROR: 'danger',
};

const TONE_TEXT: Record<Tone, string> = {
  ok: 'text-ok',
  warn: 'text-warn',
  danger: 'text-danger',
  info: 'text-info',
  idle: 'text-ink-subtle',
};

const TONE_CHIP: Record<Tone, string> = {
  ok: 'bg-ok-soft text-ok',
  warn: 'bg-warn-soft text-warn',
  danger: 'bg-danger-soft text-danger',
  info: 'bg-info-soft text-info',
  idle: 'bg-surface-sunken text-ink-muted',
};

/**
 * Takes the translator rather than calling the hook, so callers that already
 * hold one - column definitions, chart tooltips - can label a status without
 * this becoming a component.
 */
export function statusMeta(status: SessionStatus, t: Translate) {
  const known = status in TONE ? status : SessionStatus.ERROR;
  return {
    tone: TONE[known],
    label: t(`status.${known}` as MessageKey),
    hint: t(`status.${known}.hint` as MessageKey),
  };
}

export function StatusDot({
  status,
  className,
}: {
  status: SessionStatus;
  className?: string;
}) {
  const tone = TONE[status] ?? TONE.ERROR;
  // Only a genuinely live session animates. Everything else is a static dot -
  // a screen of pulsing indicators tells you nothing.
  const live = status === SessionStatus.CONNECTED;

  return (
    <span
      className={cn('relative inline-flex h-1.5 w-1.5 shrink-0', TONE_TEXT[tone], className)}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {live && <span className="pulse-dot absolute inset-0" aria-hidden />}
    </span>
  );
}

export function SessionStatusBadge({
  status,
  showHint = false,
  className,
}: {
  status: SessionStatus;
  showHint?: boolean;
  className?: string;
}) {
  const { t } = useI18n();
  const meta = statusMeta(status, t);

  return (
    <span
      title={showHint ? undefined : meta.hint}
      className={cn(
        'inline-flex h-[18px] items-center gap-1.5 rounded-sm px-1.5 text-2xs font-medium',
        TONE_CHIP[meta.tone],
        className,
      )}
    >
      <StatusDot status={status} className="text-current" />
      {meta.label}
      {showHint && <span className="font-normal opacity-70">· {meta.hint}</span>}
    </span>
  );
}
